/**
 * AdminLogistics — 物流发货看板
 * 物流记录由生产看板切换"包装待发"时自动创建（status=pending）
 * 本页面负责：填写发货信息、确认发货、确认签收
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw, Search, AlertCircle, Truck,
  CheckCircle2, Clock, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const ROW_HOVER = 'rgba(26,35,55,0.04)';

type LogisticsStatus = 'pending' | 'shipped' | 'delivered';

interface LogisticsRecord {
  id: string;
  order_id: string;
  carrier: string | null;
  tracking_no: string | null;
  shipped_at: string | null;
  estimated_arrival: string | null;
  status: LogisticsStatus;
  notes: string | null;
  created_at: string;
  order_no?: string;
  total_amount?: number | null;
}

const STATUS_CFG: Record<LogisticsStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  pending:   { label: '待发货', color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/30', icon: Clock },
  shipped:   { label: '已发货', color: 'text-blue-400',   bg: 'bg-blue-400/10 border-blue-400/30',     icon: Truck },
  delivered: { label: '已签收', color: 'text-green-400',  bg: 'bg-green-400/10 border-green-400/30',   icon: CheckCircle2 },
};

const CARRIERS = ['顺丰速运', '德邦物流', '中通快递', '韵达快递', '海运', '陆运整车', '其他'];

const inputStyle: React.CSSProperties = {
  background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337',
  borderRadius: '4px', padding: '8px 12px', fontSize: '13px', outline: 'none', width: '100%',
};

export function LogisticsView() {
  const [records, setRecords] = useState<LogisticsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  // 内联发货表单 state，key 为 logistics.id
  const [shipForms, setShipForms] = useState<Record<string, {
    carrier: string; tracking_no: string; shipped_at: string; estimated_arrival: string; notes: string;
  }>>({});

  const fetchRecords = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);

    const { data } = await supabase
      .from('logistics')
      .select('*, orders(order_no, total_amount)')
      .order('created_at', { ascending: false });

    if (data) {
      setRecords(data.map((r: Record<string, unknown>) => {
        const order = r.orders as Record<string, unknown> | null;
        return {
          ...r,
          order_no: order?.order_no as string | undefined,
          total_amount: order?.total_amount as number | null | undefined,
        } as LogisticsRecord;
      }));
    }
    setLoading(false); setRefreshing(false);
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // 初始化内联表单（当展开一条 pending 记录时）
  const initShipForm = (rec: LogisticsRecord) => {
    if (shipForms[rec.id]) return;
    setShipForms(prev => ({
      ...prev,
      [rec.id]: {
        carrier: rec.carrier && rec.carrier !== '待填写' ? rec.carrier : '',
        tracking_no: rec.tracking_no ?? '',
        shipped_at: new Date().toISOString().slice(0, 16),
        estimated_arrival: '',
        notes: rec.notes ?? '',
      },
    }));
  };

  // 确认发货
  const handleShip = async (rec: LogisticsRecord) => {
    const form = shipForms[rec.id];
    if (!form?.carrier) { alert('请选择物流公司'); return; }
    setSavingId(rec.id);
    await supabase.from('logistics').update({
      carrier: form.carrier,
      tracking_no: form.tracking_no || null,
      shipped_at: form.shipped_at ? new Date(form.shipped_at).toISOString() : new Date().toISOString(),
      estimated_arrival: form.estimated_arrival || null,
      notes: form.notes || null,
      status: 'shipped',
    }).eq('id', rec.id);

    // 联动更新订单+线索状态
    await supabase.from('orders').update({ order_status: 'shipped' }).eq('id', rec.order_id);
    const { data: ord } = await supabase.from('orders').select('inquiry_id').eq('id', rec.order_id).single();
    if (ord?.inquiry_id) await supabase.from('inquiries').update({ status: 'shipped' }).eq('id', ord.inquiry_id);

    await fetchRecords(false);
    setExpandedId(null);
    setSavingId(null);
  };

  // 确认签收
  const handleDeliver = async (rec: LogisticsRecord) => {
    if (!confirm('确认该订单已签收？')) return;
    setSavingId(rec.id);
    await supabase.from('logistics').update({ status: 'delivered' }).eq('id', rec.id);
    await supabase.from('orders').update({ order_status: 'completed' }).eq('id', rec.order_id);
    const { data: ord } = await supabase.from('orders').select('inquiry_id').eq('id', rec.order_id).single();
    if (ord?.inquiry_id) await supabase.from('inquiries').update({ status: 'completed' }).eq('id', ord.inquiry_id);
    await fetchRecords(false);
    setSavingId(null);
  };

  const filtered = records.filter(r => !search ||
    [r.order_no ?? '', r.carrier ?? '', r.tracking_no ?? ''].some(v =>
      v.toLowerCase().includes(search.toLowerCase())
    )
  );

  const counts = {
    pending: records.filter(r => r.status === 'pending').length,
    shipped: records.filter(r => r.status === 'shipped').length,
    delivered: records.filter(r => r.status === 'delivered').length,
  };

  return (
    <div className="space-y-6">
      {/* 状态总览 */}
      <div className="grid grid-cols-3 gap-3">
        {(['pending', 'shipped', 'delivered'] as LogisticsStatus[]).map(s => {
          const cfg = STATUS_CFG[s];
          const Icon = cfg.icon;
          return (
            <div key={s} className="rounded-sm border border-border p-3" style={{ background: CARD_BG }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                <span className="text-[10px] font-mono" style={{ color: '#5A6780' }}>{cfg.label}</span>
              </div>
              <div className="text-2xl font-black" style={{ color: counts[s] > 0 ? ORANGE : '#3A4A5C' }}>
                {counts[s]}
              </div>
            </div>
          );
        })}
      </div>

      {/* 搜索 + 刷新 */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#5A6780' }} />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索订单号、物流公司、运单号…"
            className="w-full pl-9 pr-4 py-2 rounded-sm text-sm outline-none"
            style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }} />
        </div>
        <button onClick={() => fetchRecords(true)} disabled={refreshing}
          className="p-2 rounded-sm border border-border disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: '#5A6780' }} />
        </button>
      </div>

      {/* 记录列表 */}
      {loading ? (
        <div className="p-16 flex justify-center">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-16 flex flex-col items-center gap-3 rounded-sm border border-border"
          style={{ background: CARD_BG, color: '#5A6780' }}>
          <AlertCircle className="w-10 h-10" />
          <span>暂无物流记录</span>
          <span className="text-xs text-center">当生产看板将任务推进到「包装待发」时，系统将自动在此生成记录</span>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map(rec => {
              const cfg = STATUS_CFG[rec.status];
              const Icon = cfg.icon;
              const isExpanded = expandedId === rec.id;
              const form = shipForms[rec.id];

              return (
                <motion.div key={rec.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
                  {/* 记录头 */}
                  <div className="flex items-center gap-4 px-5 py-4"
                    style={{ cursor: rec.status !== 'delivered' ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (rec.status === 'delivered') return;
                      const next = isExpanded ? null : rec.id;
                      setExpandedId(next);
                      if (next && rec.status === 'pending') initShipForm(rec);
                    }}
                    onMouseEnter={e => { if (rec.status !== 'delivered') (e.currentTarget as HTMLElement).style.background = ROW_HOVER; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-bold font-mono" style={{ color: ORANGE }}>
                          {rec.order_no ?? '—'}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-sm border ${cfg.bg} ${cfg.color}`}>
                          <Icon className="w-3 h-3" />{cfg.label}
                        </span>
                        {rec.total_amount != null && (
                          <span className="text-xs" style={{ color: '#5A6780' }}>
                            ¥{Number(rec.total_amount).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {rec.carrier && rec.carrier !== '待填写' && (
                        <div className="text-xs mt-1" style={{ color: '#5A6780' }}>
                          {rec.carrier}{rec.tracking_no ? ` · ${rec.tracking_no}` : ''}
                          {rec.shipped_at ? ` · 发货：${new Date(rec.shipped_at).toLocaleDateString('zh-CN')}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2" style={{ color: '#5A6780' }}>
                      <span className="text-xs hidden sm:block">{new Date(rec.created_at).toLocaleDateString('zh-CN')}</span>
                      {rec.status === 'shipped' && (
                        <button
                          onClick={e => { e.stopPropagation(); handleDeliver(rec); }}
                          disabled={savingId === rec.id}
                          className="text-xs font-bold px-3 py-1 rounded-sm disabled:opacity-50"
                          style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>
                          {savingId === rec.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : '确认签收'}
                        </button>
                      )}
                      {rec.status === 'pending' && (
                        isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </div>

                  {/* 展开：发货表单（仅 pending） */}
                  <AnimatePresence>
                    {isExpanded && rec.status === 'pending' && form && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} className="border-t border-border px-5 py-5"
                        style={{ background: '#F7F8FA' }}>
                        <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#5A6780' }}>
                          录入发货信息
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* 物流公司 */}
                          <div className="space-y-1">
                            <label className="text-xs" style={{ color: '#5A6780' }}>物流公司 *</label>
                            <select value={form.carrier}
                              onChange={e => setShipForms(prev => ({ ...prev, [rec.id]: { ...prev[rec.id], carrier: e.target.value } }))}
                              style={inputStyle}>
                              <option value="">请选择</option>
                              {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          {/* 运单号 */}
                          <div className="space-y-1">
                            <label className="text-xs" style={{ color: '#5A6780' }}>运单号</label>
                            <input type="text" value={form.tracking_no} placeholder="请输入运单号"
                              onChange={e => setShipForms(prev => ({ ...prev, [rec.id]: { ...prev[rec.id], tracking_no: e.target.value } }))}
                              style={inputStyle} />
                          </div>
                          {/* 发货时间 */}
                          <div className="space-y-1">
                            <label className="text-xs" style={{ color: '#5A6780' }}>发货时间</label>
                            <input type="datetime-local" value={form.shipped_at}
                              onChange={e => setShipForms(prev => ({ ...prev, [rec.id]: { ...prev[rec.id], shipped_at: e.target.value } }))}
                              style={inputStyle} />
                          </div>
                          {/* 预计到达 */}
                          <div className="space-y-1">
                            <label className="text-xs" style={{ color: '#5A6780' }}>预计到达</label>
                            <input type="date" value={form.estimated_arrival}
                              onChange={e => setShipForms(prev => ({ ...prev, [rec.id]: { ...prev[rec.id], estimated_arrival: e.target.value } }))}
                              style={inputStyle} />
                          </div>
                          {/* 备注 */}
                          <div className="space-y-1 md:col-span-2">
                            <label className="text-xs" style={{ color: '#5A6780' }}>备注</label>
                            <input type="text" value={form.notes} placeholder="可选"
                              onChange={e => setShipForms(prev => ({ ...prev, [rec.id]: { ...prev[rec.id], notes: e.target.value } }))}
                              style={inputStyle} />
                          </div>
                        </div>
                        <div className="flex justify-end mt-5">
                          <button onClick={() => handleShip(rec)} disabled={savingId === rec.id}
                            className="flex items-center gap-2 text-sm font-bold px-6 py-2 rounded-sm disabled:opacity-60"
                            style={{ background: ORANGE, color: '#fff' }}>
                            {savingId === rec.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                            确认发货
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
